import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { AgentCounterDataObj } from './agent-counter.model';
import { AgentCounterService } from './agent-counter.service';
import { ChartConfiguration } from 'chart.js';
import { resolve } from 'chart.js/helpers';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { Partners } from 'src/AngularJS/Admin/Company/company.model';
import { BaseChartDirective } from 'ng2-charts';
import { DatePipe } from '@angular/common';
import { ClearViewUtils } from 'src/shared/utils';

@Component({
    selector: 'cv-agent-counter',
    templateUrl: './agent-counter.component.html',
    styleUrls: ['./agent-counter.component.scss']
})

export class AgentCounterComponent implements OnInit {

    @Input() moduleKey: string;
    @ViewChild(BaseChartDirective) chartObject: BaseChartDirective;
    @Input() private moduleManager: ModuleManager;

    totalLabelSize: number;
    state = null;
    isFirstLoad = true;
    data: AgentCounterDataObj = null;
    dataLoading = false;
    dataLoadSuccess = false;
    // Chart.js Setup
    chartConfig = null;
    chartType = 'doughnut';
    chartPadding = {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
    };
    elementContext = null;

    _chartConstants = {
        colors: [ // This list is in the order of Chart dataset backgroundColor values
            "rgba(39,138,51,1)", // Available
            "rgba(125,59,200,1)", // Inbound
            "rgba(239,167,22,1)", // Outbound
            "rgba(240,93,33,1)", // ACW
            "rgba(204,8,22,1)", // Unavailable
            "rgba(227,28,121,1)", // Waiting
            "rgba(70,58,50,1)", // Preview
            "rgba(0,124,190,1)", // Dialer
        ],
        fontColor: "#353a40", // theUpsideDown color
        fontFamily: "'Lato-Regular', 'Helvetica Neue', 'Helvetica', 'Arial', 'sans-serif'", // Includes fallbacks (from default ChartJS config)
        fontSize: 13,
    };

    _chartColorsNonApiSourced = {
        colors: [ // This list is in the order of Chart dataset backgroundColor values
            "rgba(39,138,51,1)", // Available
            "rgba(125,59,200,1)", // Inbound
            "rgba(239,167,22,1)", // Outbound
            "rgba(240,93,33,1)", // ACW
            "rgba(204,8,22,1)", // Unavailable
            "rgba(227,28,121,1)", // Waiting
            "rgba(70,58,50,1)", // Preview
            "rgba(0,124,190,1)", // Dialer
        ],
    };

    _cxoneChartConstants = {
        colors: [ // This list is in the order of Chart dataset backgroundColor values
            "rgb(70, 136, 62)", // Available
            "rgb(208, 110, 67)", // Available & Dialer"
            "rgb(187, 41, 36)", // Unavailable
            "rgb(53, 122, 185)", // Working Contacts
            "rgb(117, 64, 193)", // Working Contacts & Dialer
        ],
        colorsLight: [ // This list is in the order of Chart dataset backgroundColor values
            "rgba(70, 136, 62, 0.5)", // Available
            "rgba(208, 110, 67, 0.5)", // Available & Dialer
            "rgba(187, 41, 36, 0.5)", // Unavailable
            "rgba(53, 122, 185, 0.5)", // Working Contacts
            "rgba(117, 64, 193, 0.5)", // Working Contacts & Dialer
        ],
        borderColor: 'rgba(121, 138, 146, 1)', // dark grey
        fontColor: "#353a40", // theUpsideDown color
        fontFamily: "'OpenSans-SemiBold', 'Lato-Regular', 'Helvetica Neue', 'Helvetica', 'Arial', 'sans-serif'", // Includes fallbacks (from default ChartJS config)
        fontSize: 13,
    };


    constructor(
        private agentCounterService: AgentCounterService,
        private cvLocalizationService: CVLocalizationService,
        private config: CvConfigurationService,
        private datePipe: DatePipe,
        private navigationService: NavigationService,
    ) { }

    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {

        const localizationPromises = [
            this.cvLocalizationService.GetSection("Agent_Counter_Module"),
            this.cvLocalizationService.GetSection("Module"),
        ];
        await Promise.all(localizationPromises);
        this.moduleManager.setOptions({
            onSettingsApplied: settings => this.loadData(false),
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.VeryShort,
            onRefresh: isAutoRefresh => { return this.loadData(isAutoRefresh); },
            canRefresh: () => !this.dataLoading,
        });

        this.moduleManager.registerEvents({
            onResize: () => this.setTotalLabelSize(),
        });

        // Example menu item, can be removed
        // See ModuleContainer.js addMenuItem() for more on menu items
        this.moduleManager.addMenuItem({
            name: "Legend_Position",
            icon: 'legend',
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.chartObject != null,
            subItems: [{
                name: "Top", // Menu items are localized automatically by Module Container.  Keys under section Module_Menu
                icon: 'positionTop',
                click: () => this.setLegendPosition('top'),
            },
            {
                name: "Bottom",
                icon: 'positionBottom',
                click: () => this.setLegendPosition('bottom')
            },
            {
                name: "Left",
                icon: 'positionLeft',
                click: () => this.setLegendPosition('left')
            },
            {
                name: "Right",
                icon: 'positionRight',
                click: () => this.setLegendPosition('right')
            }]
        } as ModuleManagerSubMenu);

        this.moduleManager.addState({
            name: 'chartState',
            autoLoad: true,
            getStateData: () => this.state,
            onStateLoaded: (stateData) => {
                if (!stateData)
                    return;

                this.state = stateData;
            }
        });
        
        this._chartConstants = this.config.isCxOne ? this._cxoneChartConstants : this._chartConstants;

        this.moduleManager.customSettingsAdded = true;
    }

    async loadData(autoRefresh) {
        if (this.dataLoading)
            return; // don't have multiple outgoing requests
        this.dataLoading = true;

        if (!autoRefresh)
            this.moduleManager.showLoader = true;

        if (this.state == null || this.state.legendPosition == null) {
            this.state = {
                legendPosition: 'top',
                dataPadding: '5% 0 0 0'
            };
        }

        try {
            this.data = await this.agentCounterService.LoadData(this.moduleKey, autoRefresh, this.isFirstLoad);

            this.createChart();
            this.setTotalLabelSize();
            this.dataLoadSuccess = true;
            this.isFirstLoad = false;
            this.moduleManager.setAutoRefreshReady();

            this.moduleManager.showLoader = false;
        }
        catch (err) {
            this.moduleManager.handleLoadError(err, autoRefresh);
            return;
        }
        finally {
            this.dataLoading = false;
        }
    }

    drawCenterLabel(chart) {
        if (chart.config.options.elements.center) {
            //Get ctx from string
            const ctx = chart.ctx;

            //Get options from the center object in options
            const centerConfig = chart.config.options.elements.center;
            const fontStyle = centerConfig.fontStyle || this._chartConstants.fontFamily;

            // total the shown data
            let total = 0;
            try {
                const datasetMeta = this.chartObject.chart.getDatasetMeta(0);
                datasetMeta.data.forEach((data, i) => {
                    if (!this.chartObject.chart.getDataVisibility(i)) { // ignore hidden data
                        return;
                    }

                    const dataValue = this.getDataByIndex(i) || 0;
                    total += dataValue;
                });

            } catch(e) { /* total will be 0 if no datasets */ }
            const txt = total.toString(); // agent Number
            let txt2 = this.cvLocalizationService.getCachedText("Module", "State_Agents", "Agents");
            
            if (total === 1) {
                txt2 = this.cvLocalizationService.getCachedText("Module", "Hold_Agent_Header", "Agent");
            }
            const color = centerConfig.color || this._chartConstants.fontColor;
            const sidePadding = centerConfig.sidePadding || 20;
            const innerRadius = chart._metasets[0].data[0].innerRadius; 

            const sidePaddingCalculated = (sidePadding/100) * (innerRadius * 2);
            //Start with a base font of 30px
            ctx.font = "30px " + fontStyle;

            //Get the width of the string and also the width of the element minus 10 to give it 5px side padding
            const stringWidth = ctx.measureText(txt).width;
            const stringWidth2 = ctx.measureText(txt2).width;
            const elementWidth = (innerRadius * 2) - sidePaddingCalculated;

            // Find out how much the font can grow in width.
            const widthRatio = elementWidth / stringWidth;
            const widthRatio2 = elementWidth / stringWidth2;

            const newFontSize = this.config.isCxOne ? Math.floor(25 * widthRatio2) : Math.floor(27 * widthRatio); // using text to determine number size
            const newFontSize2 = Math.floor(newFontSize * 0.7); 

            const elementHeight = (innerRadius * 2);

            // Pick a new font size so it will not be larger than the height of label.
            const fontSizeToUse = Math.min(newFontSize, elementHeight);
            const fontSizeToUse2 = Math.min(newFontSize2, elementHeight);


            // Set font settings to draw it correctly.
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const centerX = ((chart.chartArea.left + chart.chartArea.right) / 2);
            const centerY = ((chart.chartArea.top + chart.chartArea.bottom) / 2);
            ctx.font = fontSizeToUse + "px " + fontStyle;
            ctx.fillStyle = color;

            if (this.config.isCxOne) {
                // Draw number in center
                ctx.fillText(txt, centerX, centerY - (fontSizeToUse2 / 2) - 6);

                ctx.font = fontSizeToUse2 + "px " + fontStyle;

                // Draw Agents text in center
                ctx.fillText(txt2, centerX, centerY + (fontSizeToUse2 / 2) + 6); // (fontSizeToUse2/2) + 5 is to offset slightly from center so texts stack
            }
            else {
                ctx.fillText(txt, centerX, centerY);
            }
        }
    }

    // /**
    //  * This function calculates the normalized weights of each slice, with a minimum weight to each slice.
    //  * @param {[number, number, number, number]} dataObj Four-number array for Available, Inbound, Outbound, Unavailable weights (in that order)
    //  */
    calcWeightsWithMinSize(dataObj: AgentCounterDataObj) {
        const threshold = 0.11; // Minimum weight of 11%
        let values: number[];

        if (dataObj.agentCounterDataQE) {
            values = [
                dataObj.agentCounterDataQE.Available.agentCount,
                dataObj.agentCounterDataQE.AvailableAndDialer.agentCount,
                dataObj.agentCounterDataQE.Unavailable.agentCount,
                dataObj.agentCounterDataQE.WorkingContacts.agentCount,
                dataObj.agentCounterDataQE.WorkingContactsAndDialer.agentCount
            ];
        } else {
            values = [
                dataObj.agentCounterDataPM.Available,
                dataObj.agentCounterDataPM.Inbound,
                dataObj.agentCounterDataPM.Outbound,
                dataObj.agentCounterDataPM.Acw,
                dataObj.agentCounterDataPM.Unavailable,
                dataObj.agentCounterDataPM.Dialer
            ];
    
            if (dataObj.FromApiSourcedRs) {
                values.splice(values.length - 1, 0, dataObj.agentCounterDataPM.Waiting);
                values.splice(values.length - 1, 0, dataObj.agentCounterDataPM.Preview);
            }
        }

        const valuesSum = values.reduce((sum, v) => sum + v, 0);
        if (valuesSum <= 0) {
            for (let i = 0; i < values.length; i++) {
                values[i] = 0;
            }
        }
        const normalizedValues = values.map((v) => v / valuesSum );
        const aboveThresholdIndices = []; // List of indices (of the values array) that are above the threshold.  These will be reduced when there are values below the threshold
        let aboveThresholdSum = 0; // Sum of all the values above the threshold.  This is used to figure out the proportion to reduce each value by.
        let belowThresholdDiffSum = 0; // For values below the threshold, this variable is the sum of all the (threshold - value) differences
        for (let i = 0; i < normalizedValues.length; i++) {
            if (normalizedValues[i] > 0 && normalizedValues[i] < threshold) {
                belowThresholdDiffSum += threshold - normalizedValues[i];
                normalizedValues[i] = threshold;
            } else if (normalizedValues[i] > 0 && normalizedValues[i] > threshold) {
                aboveThresholdSum += normalizedValues[i];
                aboveThresholdIndices.push(i);
            }
        }
        // Proportionally reduce weights of those above the threshold.  Larger chunks get reduced more.
        aboveThresholdIndices.forEach( i => {
            const relativeSize = normalizedValues[i] / aboveThresholdSum;
            normalizedValues[i] -= relativeSize * belowThresholdDiffSum;
        });
        return normalizedValues;
    }

    /**
     * Returns the value from ctrl.data pertaining to ChartJS's dataset index.
     * 0 = Available, 1 = Inbound, 2 = Outbound, 3 = Acw, 4 = Unavailable
     */
    getDataByIndex(index) {
        if (this.data && this.data.agentCounterDataQE) {
            switch (index) {
                case 0:
                    return this.data.agentCounterDataQE.Available.agentCount;
                case 1:
                    return this.data.agentCounterDataQE.AvailableAndDialer.agentCount;
                case 2:
                    return this.data.agentCounterDataQE.Unavailable.agentCount;
                case 3:
                    return this.data.agentCounterDataQE.WorkingContacts.agentCount;
                case 4:
                    return this.data.agentCounterDataQE.WorkingContactsAndDialer.agentCount;
            }
        } else {
            switch (index) {
                case 0:
                    return this.data && this.data.agentCounterDataPM.Available;
                case 1:
                    return this.data && this.data.agentCounterDataPM.Inbound;
                case 2:
                    return this.data && this.data.agentCounterDataPM.Outbound;
                case 3:
                    return this.data && this.data.agentCounterDataPM.Acw;
                case 4:
                    return this.data && this.data.agentCounterDataPM.Unavailable;
                case 5:
                    return this.data && this.data.agentCounterDataPM.Waiting;
                case 6:
                    return this.data && this.data.agentCounterDataPM.Preview;
                case 7:
                    return this.data && this.data.agentCounterDataPM.Dialer;
            }
        }
    }

    /**
     * Determines if the chart should include the Dialer section based on the company partner.
     * @returns True if the chart should include the Dialer section, false otherwise.
     */
    shouldChartIncludeDialer(): boolean {
        const excludedPartners = [Partners.Five9, Partners.UJet];
        return !excludedPartners.includes(this.navigationService.companyPartner);
    }

    createChart() {
        // Check for and destroy any existing charts
        if (this.state == null || this.state.legendPosition == null) {
            this.state = {
                legendPosition: 'top',
                dataPadding: '5% 0 0 0'
            };
        }

        // Check for null/empty datasets
        // If found, create an empty chart
        if (this.data === null) {
            return;
        }

        //
        // Make Chart
        //
        const agentstateCategories = this.data.AgentStateCategories;
        let preparedLabels: string[];
        if (this.data.agentCounterDataQE) {
            preparedLabels = [
                agentstateCategories["Available"],
                agentstateCategories["AvailableDialer"],
                agentstateCategories["Unavailable"],
                agentstateCategories["WorkingContacts"],
                agentstateCategories["WorkingContactsDialer"],
            ];
        }
        else {
            preparedLabels = [
                agentstateCategories["Available"],
                agentstateCategories["Inbound"],
                agentstateCategories["Outbound"],
                this.getLocalizedPartnerLabel("ACW"),
                agentstateCategories["Unavailable"],
            ];
            if (this.shouldChartIncludeDialer()) {
                preparedLabels.push(this.getLocalizedPartnerLabel("Dialer"));
            }
            if (this.data.FromApiSourcedRs) {
                preparedLabels.splice(preparedLabels.length - 1, 0, this.getLocalizedPartnerLabel("Waiting"));
                preparedLabels.splice(preparedLabels.length - 1, 0, this.getLocalizedPartnerLabel("Preview"));
            }
        }

        this.chartConfig = {
            type: 'doughnut',
            data: {
                datasets: [{
                    data: this.calcWeightsWithMinSize(this.data),
                    backgroundColor: this.data.FromApiSourcedRs ? this._chartConstants.colors : this._chartColorsNonApiSourced.colors,
                    borderColor: this.config.isCxOne ? this._cxoneChartConstants.borderColor : undefined,
                    borderWidth: this.config.isCxOne ? 1 : 5,
                    hoverBackgroundColor: this.config.isCxOne ? this._cxoneChartConstants.colorsLight : undefined,
                    hoverBorderWidth: this.config.isCxOne ? 1 : 0,
                }],
                labels: preparedLabels
            },
            options: {
                cutout: this.config.isCxOne ? '76%' : undefined, // makes ring thicker and thinner 76 makes it 48 px thick
                responsive: true,
                maintainAspectRatio: false,
                elements: {
                    center: {
                        text: "", // The actual content of the center is drawn by the beforeDraw custom plugin below
                        color: this._chartConstants.fontColor,
                        fontFamily: this._chartConstants.fontFamily,
                    },
                },
                plugins: {
                    emptyChart: {
                        disableCxone: this.config.isCxOne,
                    },
                    tooltip: {
                        padding: this.config.isCxOne ? 12 : 6,
                        usePointStyle: true,
                        boxPadding: this.config.isCxOne ? 3 : 1,
                        boxWidth: this.config.isCxOne ? 11 : 18,
                        boxHeight: this.config.isCxOne ? 11 : 18,
                        callbacks: {
                            title: tooltipItemArray =>
                                this.config.isCxOne ? this.datePipe.transform(new Date(), "MMM dd, yyyy") : "",
                            label: tooltipItem => {
                                return tooltipItem.label;
                            },
                            footer: tooltipItem => {
                                const numInState = this.getDataByIndex(tooltipItem[0].dataIndex);
                                const total = this.data.agentCounterDataQE ? this.data.agentCounterDataQE.Total : this.data.agentCounterDataPM.Total;
                                const percent = numInState / total * 100;
                                const percentFormat = percent.toFixed(2);
                                const cxoneFooter = '    ' + percentFormat + '%';
                                //for PM footer
                                const avgTxt = this.cvLocalizationService.getCachedText("Agent_Counter_Module", "Average", "Average");
                                const countTxt = this.cvLocalizationService.getCachedText("Agent_Counter_Module", "Count", "Count")
                                const pmFooter = `${avgTxt}: ${percentFormat}% \n${countTxt}: ${numInState}`;
                                return this.config.isCxOne ? cxoneFooter : pmFooter;
                            },
                            labelColor: context => ({
                                borderColor: 'rgb(255, 255, 255)',
                                backgroundColor: context.dataset.backgroundColor[context.dataIndex],
                                borderWidth: this.config.isCxOne ? 0 : 0.5,
                            }),
                            labelPointStyle: (context) => ({
                                pointStyle: this.config.isCxOne ? 'circle' : 'rect',
                                rotation: 0,
                            }),
                        },
                        backgroundColor: this.config.isCxOne ? '#FFF' : 'rgba(0, 0, 0, 0.8)',
                        titleFont: {
                            size: this.config.isCxOne ? 12 : 16,
                            weight: this.config.isCxOne ? "normal" : "bold",
                        },
                        titleColor: this.config.isCxOne ? '#8c95a5' : '#fff',
                        bodyColor: this.config.isCxOne ? '#353a40' : '#fff',
                        footerColor: this.config.isCxOne ? '#353a40' : '#fff',
                        bodyFont: {
                            size: 14,
                            weight: this.config.isCxOne ? "normal" : "bold",
                        },
                        footerFont: {
                            size: 14,
                            weight: this.config.isCxOne ? "bold" : "normal",
                        },
                        borderColor: this.config.isCxOne ? '#D0D2D3' : '#353a40',
                        borderWidth: this.config.isCxOne ? 1 : 0,
                        cornerRadius: this.config.isCxOne ? 0 : 6,
                    },
                    legend: {
                        display: true,
                        position: this.state.legendPosition,
                        labels: {
                            usePointStyle: !this.config.isCxOne ? true : undefined,
                            boxWidth: 13,
                            font: {
                                family: this._chartConstants.fontFamily,
                                size: this._chartConstants.fontSize,
                            },
                            color: this._chartConstants.fontColor,
                            //If we are on PM we don't want the custom generated labels, hence we are setting undefined here
                            generateLabels: !this.config.isCxOne ? undefined : (chart) => { // adds custom labels to the chart legend 
                                const data = chart.data;
                                if (data.labels.length && data.datasets.length) {
                                    return data.labels.map((label, i) => {
                                        const meta = chart.getDatasetMeta(0);
                                        const ds = data.datasets[0];
                                        const arc = meta.data[i];
                                        const custom = arc && arc.options || {};
                                        const arcOpts = chart.options.elements.arc;

                                        const fill = custom.backgroundColor ? custom.backgroundColor :
                                            resolve([ds.backgroundColor, arcOpts.backgroundColor], chart.ctx , i);
                                        const stroke = custom.borderColor ? custom.borderColor :
                                            resolve([ds.borderColor, arcOpts.borderColor], chart.ctx, i);
                                        const bw = custom.borderWidth ? custom.borderWidth :
                                            resolve([ds.borderWidth, arcOpts.borderWidth], chart.ctx, i);

                                        // We get the value of the current label
                                        const value = this.getDataByIndex(i);
                                        const toggle = chart.getDataVisibility(i);
                                        return {
                                            // Instead of `text: label,`
                                            // We add the value to the string
                                            text: label + " (" + value + ")",
                                            fillStyle: fill,
                                            strokeStyle: stroke,
                                            lineWidth: bw,
                                            hidden: !toggle,
                                            index: i
                                        };
                                    });
                                }
                            },
                        }
                    },
                },
                layout: {
                    padding: {
                        left: 8,
                        right: 8,
                        top: 8,
                        bottom: 8
                    }
                },
                animation: {
                    duration: 0,
                },
                hover: {
                    animationDuration: 0,
                },
            },
            plugins: [{
                id: "agent-counter-center-label",
                //white numbers on slices for PM 
                afterDatasetsDraw: this.config.isCxOne ? undefined : chartInstance => { // sets the values to the true numbers, rather than the normalized size of each slice.
                    const ctx = chartInstance.ctx;
                    const width = chartInstance.width;
                    const size = Math.round(width / 20);
                    chartInstance.data.datasets.forEach((dataset, i) => {
                        const meta = chartInstance.getDatasetMeta(i);
                        if (!meta.hidden) {
                            meta.data.forEach((element, index) => {
                                if (!chartInstance.getDataVisibility(index))
                                    return;
                                const trueValue = this.getDataByIndex(index);
                                // added if statement so text of zero wouldnt appear on the pie chart with no slice space.
                                if (trueValue === 0)
                                    return;
                                ctx.fillStyle = "#fff";
                                ctx.font = "bold " + size + "px " + this._chartConstants.fontFamily;
                                ctx.textAlign = "center";
                                ctx.textBaseline = "middle";
                                const position = element.tooltipPosition(true);
                                ctx.fillText(trueValue.toString(), position.x, position.y);
                            });
                        }
                    });
                },
                beforeDraw: (chart): void => {
                    requestAnimationFrame(() => this.drawCenterLabel(chart))
                },
            }],
        } as ChartConfiguration;
        this.chartConfig.data.datasets[0].data = this.calcWeightsWithMinSize(this.data);
    }

    async setLegendPosition(position) {
        this.state.legendPosition = position;
        this.chartConfig.options.plugins.legend.position = position;
        this.loadData(false);
        await this.moduleManager.saveState('chartState');
        this.moduleManager.requestPublishNeeded();
    }

    setTotalLabelSize() {
        this.totalLabelSize = this.chartObject?.chart.width / 9;
    }

    /**
     * Gets the localized version of a partners label.
     * @param label the label display value from AgentStateCategories.
     * @returns Localized label.
     */
    private getLocalizedPartnerLabel(label: string): string {
        let key = label.replace(/\s&\s/g, ' '); // Remove " & " (space, ampersand, space).
        key = ClearViewUtils.standardizeLocalizationKey(key);

        if (/^[A-Z0-9-_]$/.test(key))
            key += "_C";

        return this.cvLocalizationService.getCachedText("Agent_Counter_Module", key, label);
    }
}
